import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Loader2, Sparkles } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useGenerationQuota, formatResetDate } from "@/hooks/useGenerationQuota";
import { useSubscription } from "@/hooks/useSubscription";
import { useToast } from "@/hooks/use-toast";

interface Props {
  email: string | null;
  createdAt: string | null;
}

const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });

const SubscriptionCard = ({ email, createdAt }: Props) => {
  const { used, limit, remaining, resetAt, unlimited, loading } = useGenerationQuota();
  const { isPro, isUltra, billingInterval, loading: subLoading } = useSubscription();
  const { toast } = useToast();
  const [portalBusy, setPortalBusy] = useState(false);

  const currentLabel = unlimited ? "Team" : isUltra ? "Ultra" : isPro ? "Pro" : "Free";
  const showUpgrade = !isPro && !unlimited && !subLoading;
  const limitReached = !unlimited && !loading && remaining <= 0;

  const openPortal = async () => {
    setPortalBusy(true);
    try {
      const { data, error } = await supabase.functions.invoke<{ url: string }>(
        "create-portal",
        { body: {} },
      );
      if (error || !data?.url) throw new Error(error?.message || "Couldn't open portal");
      window.open(data.url, "_blank");
    } catch (e: any) {
      toast({ title: "Couldn't open billing portal", description: e?.message, variant: "destructive" });
    } finally {
      setPortalBusy(false);
    }
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Account details */}
      <div className="bg-card border border-border p-5 sm:p-6 rounded-lg grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <p className="eyebrow mb-1">Email</p>
          <p className="text-foreground text-sm break-all">{email || "—"}</p>
        </div>
        <div>
          <p className="eyebrow mb-1">Member since</p>
          <p className="text-foreground text-sm">{createdAt ? formatDate(createdAt) : "—"}</p>
        </div>
      </div>

      {/* Usage */}
      <div className="bg-card border border-border p-5 rounded-lg space-y-3">
        <div className="flex items-center justify-between gap-4">
          <div>
            <p className="eyebrow">Current plan</p>
            <p className="text-foreground font-semibold text-sm mt-0.5">{currentLabel}</p>
          </div>
          {!unlimited && (
            <div className="text-right">
              <p className="eyebrow">This month</p>
              <p className="text-foreground font-semibold text-sm mt-0.5">
                {loading ? "—" : `${used} / ${limit} designs`}
              </p>
            </div>
          )}
        </div>
        {!unlimited && (
          <>
            <div className="h-1.5 w-full bg-muted rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-cta transition-all"
                style={{ width: `${Math.min(100, (used / Math.max(1, limit)) * 100)}%` }}
              />
            </div>
            <p className="text-xs text-muted-foreground">
              {remaining > 0
                ? `${remaining} design${remaining === 1 ? "" : "s"} remaining · resets ${formatResetDate(resetAt)}`
                : `Limit reached · resets ${formatResetDate(resetAt)}`}
            </p>
          </>
        )}
      </div>

      {/* Upgrade CTA */}
      {showUpgrade && (
        <div className="bg-gradient-cta text-primary-foreground shadow-elegant rounded-lg p-5 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-start gap-3">
            <div className="shrink-0 w-9 h-9 rounded-full bg-primary-foreground/20 flex items-center justify-center">
              <Sparkles className="w-4 h-4" />
            </div>
            <div>
              <p className="font-display font-semibold text-lg leading-tight">
                Upgrade to Huelo Pro
              </p>
              <p className="text-sm opacity-90 mt-0.5">
                {limitReached
                  ? "You've hit your free limit — unlock 20 designs/month and keep going."
                  : "20 designs/month, 2K renders, and 100 refinement tokens."}
              </p>
            </div>
          </div>
          <Link
            to="/pricing"
            className="w-full sm:w-auto shrink-0 rounded-full bg-background text-foreground px-6 py-2.5 text-sm font-semibold hover:opacity-95 transition-opacity inline-flex items-center justify-center gap-2"
          >
            See plans
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      )}

      {/* Pro manage — team accounts get Pro access without a Stripe subscription */}
      {isPro && !unlimited && (
        <div className="bg-card border border-border p-5 rounded-lg flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <p className="text-foreground text-sm font-medium">Huelo {isUltra ? "Ultra" : "Pro"}</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              {billingInterval ? `Billed ${billingInterval === "year" ? "yearly" : "monthly"}` : "Active subscription"}
            </p>
          </div>
          <button
            onClick={openPortal}
            disabled={portalBusy}
            className="w-full sm:w-auto rounded-full bg-foreground text-background px-5 py-2.5 text-sm font-semibold hover:opacity-90 transition-opacity inline-flex items-center justify-center gap-2 disabled:opacity-60"
          >
            {portalBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            Manage subscription
          </button>
        </div>
      )}
    </div>
  );
};

export default SubscriptionCard;
